import { Dtos, type SuretyAccountId } from "@inrev/common";
import { useSubmitFinancialPeriodProfitability } from "@portal/api/financials";
import { FormFieldGroup, FormRow } from "@portal/components/form";
import { FormYesNoComponent } from "@portal/components/form/fieldLayouts/FormYesNoComponent";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { createFormResolver } from "@portal/components/multiform/utils/formResolver";
import { Button } from "@portal/components/primitives";
import { FormSchemaProvider } from "@portal/providers/FormSchemaProvider";
import { useFlags } from "@portal/providers/GlobalFlagsProvider";
import type { FC } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type { ProfitabilityField } from "./types";

function buildSchema(fields: readonly ProfitabilityField[]) {
	return z
		.object({
			profitableLastFye: z.boolean().nullish(),
			profitableYtd: z.boolean().nullish(),
		})
		.superRefine((data, ctx) => {
			if (fields.includes("profitableLastFye")) {
				if (data.profitableLastFye !== true && data.profitableLastFye !== false) {
					ctx.addIssue({
						code: "custom",
						path: ["profitableLastFye"],
						message: "Select Yes or No.",
					});
				}
			}
			if (fields.includes("profitableYtd")) {
				if (data.profitableYtd !== true && data.profitableYtd !== false) {
					ctx.addIssue({
						code: "custom",
						path: ["profitableYtd"],
						message: "Select Yes or No.",
					});
				}
			}
		});
}

type ProfitabilityModalProps = {
	accountId: SuretyAccountId;
	fields: readonly ProfitabilityField[];
	onClose: () => void;
};

export const ProfitabilityModal: FC<ProfitabilityModalProps> = ({ accountId, fields, onClose }) => {
	const { mutate, isPending } = useSubmitFinancialPeriodProfitability(accountId);
	const { showErrorFlag } = useFlags();

	const schema = buildSchema(fields);
	type FormValues = z.infer<typeof schema>;

	const formMethods = useForm<FormValues>({
		mode: "onSubmit",
		resolver: createFormResolver(schema),
		defaultValues: {},
	});

	const handleSubmit = formMethods.handleSubmit((values) => {
		const payload: Dtos.Agent.FinancialPeriod.Profitability.Upsert.Request = {};
		if (fields.includes("profitableLastFye") && values.profitableLastFye != null) {
			payload.profitableLastFye = values.profitableLastFye;
		}
		if (fields.includes("profitableYtd") && values.profitableYtd != null) {
			payload.profitableYtd = values.profitableYtd;
		}
		mutate(payload, {
			onSuccess: onClose,
			onError: (error) => {
				showErrorFlag({
					heading: "There was an error saving profitability",
					message: error.message,
				});
			},
		});
	});

	return (
		<Modal onClickOutside={onClose}>
			<ModalItemWithHeader header="Add profitability" onClose={onClose}>
				<FormSchemaProvider value={schema}>
					<form
						className="flex min-w-[400px] max-w-[520px] flex-col gap-5 px-5 py-4"
						onSubmit={(e) => {
							e.preventDefault();
							handleSubmit();
						}}
					>
						<FormFieldGroup>
							<FormRow className="flex-col items-stretch gap-5 sm:flex-row sm:items-start sm:gap-10">
								{fields.includes("profitableLastFye") && (
									<FormYesNoComponent
										formMethods={formMethods}
										name="profitableLastFye"
										label="Profitable last FYE?"
										inline
										testId="profitability-last-fye"
									/>
								)}
								{fields.includes("profitableYtd") && (
									<FormYesNoComponent
										formMethods={formMethods}
										name="profitableYtd"
										label="Profitable YTD?"
										inline
										testId="profitability-ytd"
									/>
								)}
							</FormRow>
						</FormFieldGroup>
						<div className="flex justify-end gap-3 pt-1">
							<Button
								type="button"
								color="secondary"
								variant="outline"
								onClick={onClose}
								disabled={isPending}
							>
								Cancel
							</Button>
							<Button type="submit" color="primary" loading={isPending}>
								Save
							</Button>
						</div>
					</form>
				</FormSchemaProvider>
			</ModalItemWithHeader>
		</Modal>
	);
};
